import { Component, OnInit, Input, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, ElementRef, ViewChild } from '@angular/core';

import { DnaInfoComboBoxModel } from './dna-info-combo-box.model';
import { DnaInfoComboBoxService } from './api-services/dna-info-combo-box.service';

import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

@Component({
  selector: 'app-dna-info-combo-box',
  templateUrl: './dna-info-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DnaInfoComboBoxComponent,
      multi: true
    },
    {
      provide: DnaInfoComboBoxService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new DnaInfoComboBoxService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})
export class DnaInfoComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() top: number = null;
  @Input() left: number = null;
  @Input() width: number | string;
  @Input() height: number = null;
  @Input() public tabindex = Number(0);
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  // 選択されたインデックスNoを入れる初期値は-1で空白行を選択
  @Input() public selectedIndex: number = <number>-1;
  // 次のフォーカス先
  @Input() initFocusItem: any = null;
  @Input() forcedEnabledMode: string = null;

  public DnaDiv: string = <string>'';
  public DnaName: string = <string>'';
  public SortNum: string = <string>'';
  public OutputDiv: string = <string>'';
  public DnaDivName: string = <string>'';

  // モデルをインスタンス化
  public model: DnaInfoComboBoxModel = new DnaInfoComboBoxModel();

  // 選択されたものを入れる箱
  public selectedDnaDiv: string = <string>'';
  public selectedDnaName: string = <string>'';
  public selectedSortNum: string = <string>'';
  public selectedOutputDiv: string = <string>'';
  public selectedDnaDivName: string = <string>'';

  /* コンストラクタ */
  constructor(
    public elementRef: ElementRef,
    // サービス読み込み
    public dnaInfoComboBoxService: DnaInfoComboBoxService
  ) { }

  // ValueAccessor
  @Input() public disabled: boolean;
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedDnaDiv) {
      this.selectedDnaDiv = text;
      this.setCodeValue(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
  * 初期処理
  *
  * @memberof DnaInfoComboBoxComponent
  */
  ngOnInit() {
    // 自身をモデルに入れる（@inputされた値が入るblankTopなど）
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof DnaInfoComboBoxComponent
   */
  ngAfterViewInit(): void {
    // データ取得
    this.getData();
  }

  /**
   * HTMLの属性から受け取った値を受け取った場合
   *
   * @memberof DnaInfoComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
      }
    });
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @param {string} codeValue コード値
   * @memberof DnaInfoComboBoxComponent
   */
  public setCodeValue(codeValue: string): void {
    this.model.setCodeValue = codeValue;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCodeValue();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof DnaInfoComboBoxComponent
   */
  updateSelectedCodeValue() {
    // コードマスタから情報を取得する
    const codeList: Array<any> = this.model.codeList;

    // 入力値と一致するコード値があればそれを設定する
    for (let i = Number(0); i < codeList.length; i++) {
      if (codeList[i].DnaDiv === this.model.setCodeValue) {
        this.selectedIndex = i;
        this.updateBindedField(null);
        break;
      }
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @param {*} e
   * @returns
   * @memberof DnaInfoComboBoxComponent
   */
  updateBindedField(e: any) {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }

    // 選択されたものが-1（初期値）の場合すべてリセットする
    if (this.selectedIndex === -1) {
      this.selectedDnaDiv = '';
      this.selectedDnaName = '';
      this.selectedSortNum = '';
      this.selectedOutputDiv = '';
      this.selectedDnaDivName = '';
      return;
    }

    this.selectedDnaDiv = this.model.codeList[Number(this.selectedIndex)].DnaDiv;
    this.selectedDnaName = this.model.codeList[Number(this.selectedIndex)].DnaName;
    this.selectedSortNum = this.model.codeList[Number(this.selectedIndex)].SortNum;
    this.selectedOutputDiv = this.model.codeList[Number(this.selectedIndex)].OutputDiv;
    this.selectedDnaDivName = this.model.codeList[Number(this.selectedIndex)].DnaDivName;

    this.DnaDiv = this.selectedDnaDiv;
    this.DnaName = this.selectedDnaName;
    this.SortNum = this.selectedSortNum;
    this.OutputDiv = this.selectedOutputDiv;
    this.DnaDivName = this.selectedDnaDivName;

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }
  }

  /**
   * データ読み込み
   *
   * @memberof DnaInfoComboBoxComponent
   */
  public getData(): void {
    // サービスのメソッドを実行
    this.dnaInfoComboBoxService.execute(this.model, null);
  }

  /**
   * コンポーネント終了時
   *
   * @memberof DnaInfoComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * 初期化
   *
   * @memberof DnaInfoComboBoxComponent
   */
  public clear() {
    this.DnaDiv = null;
    this.DnaName = null;
    this.SortNum = null;
    this.OutputDiv = null;
    this.DnaDivName = null;
    this.model = new DnaInfoComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

}
